import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field, Textarea, PageTitle, StatusChip, Tag, Meta, cx } from "../components/kit";
import { Loading, NoAccess } from "../components/common";
import { FileThumb } from "../components/FileUploader";
import { api, parseError } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { MergeBar, MergeInfo } from "./ResponseMerge";

export default function TaskReview() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [dec, setDec] = useState({});
  const [sel, setSel] = useState([]);
  const load = useCallback(() => api.get(`/home-tasks/${id}`).then((r) => setD(r.data)).catch((e) => parseError(e).status === 404 && setDenied(true)), [id]);
  useEffect(() => { load(); }, [load]);
  if (denied) return <NoAccess />;
  if (!d) return <Loading />;
  const task = d.task;
  const decide = async (rid) => {
    const x = dec[rid] || {};
    if (!x.decision) return toast.error(t("err.required"));
    try { await api.post(`/review-tasks/${rid}/decide`, x); toast.success(t("review.recorded")); load(); } catch (e) { toast.error(t(`err.${parseError(e).code}`)); }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageTitle title={task.title} badge={<><Tag>{task.number} · {t("common.version_n", { n: task.version })}</Tag><StatusChip status={task.status} /></>}
        sub={<span><Link to={`/programmes/${task.programme_id}`} className="font-semibold underline">{task.programme_number}</Link> · <Link className="underline" to={`/dogs/${d.dog.id}`}>{d.dog.name}</Link></span>} />
      <Card className="grid gap-3 sm:grid-cols-3">
        <Meta label={t("owner.author")}>{task.author_name}</Meta><Meta label={t("task.deadline")}>{fmtDate(task.deadline)}</Meta><Meta label={t("programme.edition")}>{t("programme.edition_n", { n: task.programme_edition })}</Meta>
        <Meta label={t("home_field.stopping_conditions")}>{task.stopping_conditions}</Meta><Meta label={t("common.created")}>{fmtDateTime(task.created_at)}</Meta>
        {task.change_reason && <Meta label={t("common.reason")}>{task.change_reason}</Meta>}
      </Card>
      <h2 className="text-xl font-bold">{t("review.responses")}</h2>
      <MergeBar responses={d.responses} selected={sel} onDone={() => { setSel([]); load(); }} />
      {d.responses.length === 0 && <p className="text-muted-foreground"><StatusChip status="no_response" label={t("status.no_response")} /></p>}
      {d.responses.map((r) => {
        const rv = d.reviews.find((x) => x.response_id === r.id);
        return (
          <Card key={r.id} data-testid={`response-${r.number}`} className={cx("space-y-2", r.merged_into && "opacity-80")}>
            <div className="flex flex-wrap items-center gap-2">
              {!r.merged_into && d.responses.length > 1 && <label className="flex min-h-[44px] min-w-[44px] items-center gap-1 text-sm"><input type="checkbox" aria-label={`${t("p3.merge_select")} ${r.number}`} data-testid={`merge-select-${r.number}`} checked={sel.includes(r.id)} onChange={(e) => setSel(e.target.checked ? [...sel, r.id] : sel.filter((x) => x !== r.id))} /></label>}
              <Tag>{r.number}</Tag><StatusChip status={r.outcome} /><b>{r.owner_name}</b><span className="text-sm text-muted-foreground">{fmtDateTime(r.created_at)}</span><Tag>{t("source.owner_report")}</Tag></div>
            <MergeInfo r={r} responses={d.responses} />
            {r.context && <p>{r.context}</p>}{r.observations && <p className="text-sm text-muted-foreground">{r.observations}</p>}
            {r.file_ids?.length > 0 && <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">{r.file_ids.map((f) => <FileThumb key={f} file={{ id: f, kind: "photo", title: "" }} />)}</div>}
            {rv && rv.status === "open" && (
              <div className="rounded-xl bg-muted/60 p-3" data-testid={`review-form-${rv.number}`}>
                <p className="mb-2 text-sm font-semibold">{t("review.decision")}</p>
                <div className="flex flex-wrap gap-2">{["retain", "clarify", "revise"].map((x) => (
                  <button key={x} data-testid={`review-decision-${x}`} aria-pressed={dec[rv.id]?.decision === x} onClick={() => setDec({ ...dec, [rv.id]: { ...dec[rv.id], decision: x } })}
                    className={cx("min-h-[44px] rounded-full border px-4 text-sm font-semibold", dec[rv.id]?.decision === x ? "border-primary bg-peach" : "bg-white")}>{t(`review.${x}`)}</button>))}</div>
                <Field label={t("review.note")} htmlFor={`rn-${rv.id}`}><Textarea id={`rn-${rv.id}`} data-testid="review-note-input" value={dec[rv.id]?.note || ""} onChange={(e) => setDec({ ...dec, [rv.id]: { ...dec[rv.id], note: e.target.value } })} /></Field>
                <Btn className="mt-2" testid="review-mark-reviewed-btn" onClick={() => decide(rv.id)}>{t("review.mark")}</Btn>
              </div>
            )}
            {rv && rv.status === "done" && <p className="text-sm" data-testid="review-decision-recorded"><StatusChip status="reviewed" /> {t(`review.${rv.decision}`)} · {rv.decided_by_name} · {fmtDateTime(rv.decided_at)}</p>}
          </Card>
        );
      })}
    </div>
  );
}
